'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { FileText, BarChart3, Layers, Printer } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { stickingQueries } from '../services/sticking.queries';

export function StickingMetrics() {
  const t = useTranslations('sticking');
  const { data, isLoading } = useQuery(stickingQueries.globalMetrics());

  const metrics = data?.data;

  const cards = [
    {
      label: t('metricTodayPrinted'),
      value: metrics?.today_printed ?? 0,
      icon: BarChart3,
      color: 'text-purple-600 dark:text-purple-400',
      bgColor: 'bg-purple-50 dark:bg-purple-950'
    },
    {
      label: t('metricContainers'),
      value: metrics?.total_containers_in_indexing ?? 0,
      icon: Layers,
      color: 'text-amber-600 dark:text-amber-400',
      bgColor: 'bg-amber-50 dark:bg-amber-950'
    },
    {
      label: t('metricTotalPrinted'),
      value: metrics?.total_pages_printed ?? 0,
      icon: FileText,
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-950'
    },
    {
      label: t('metricTotalBatches'),
      value: metrics?.total_batches ?? 0,
      icon: Printer,
      color: 'text-rose-600 dark:text-rose-400',
      bgColor: 'bg-rose-50 dark:bg-rose-950'
    }
  ];

  if (isLoading) {
    return (
      <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className='h-24 w-full rounded-lg' />
        ))}
      </div>
    );
  }

  return (
    <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
      {cards.map((card) => (
        <Card key={card.label}>
          <CardHeader className='flex flex-row items-center gap-2 pb-2'>
            <div className={`rounded-full p-2 ${card.bgColor}`}>
              <card.icon className={`h-4 w-4 ${card.color}`} />
            </div>
            <CardTitle className='text-sm font-medium'>{card.label}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>
              {typeof card.value === 'number'
                ? card.value.toLocaleString()
                : card.value}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
